import React, {
    CSSProperties, ReactNode,
    useCallback,
    useEffect,
    useState
} from "react";
import {Transformable} from "../../../feature/project/Shot";
import {Position2D} from "react-move-hook/lib/types/util";
import {useMovable} from "react-move-hook";

export const ElementContainer = (props: {
    editable: boolean,
    style?: CSSProperties,
    static?: boolean,
    isSelected: boolean,
    scale?: number,
    element: Transformable,
    children: ReactNode | null | undefined,
    onPositionChanged: (position: Position2D) => void,
    onMove?: (info: { x: number, y: number, z: number, scale: number }) => void,
    onClick?: (position: Position2D) => void,
    onSelect: () => void,
    size?: { width: number, height: number }
}) => {

    const [state, setState] = useState<{
        moving: boolean,
        changed: boolean,
        position: Position2D,
        delta: Position2D | undefined
    }>({
        moving: false,
        changed: false,
        position: {
            x: (props.element.x || 0),
            y: (props.element.y || 0)
        },
        delta: undefined,
    });
    useEffect(() => {
        document.body.classList.toggle("moving", state.moving);
    }, [state.moving]);

    useEffect(() => {
        if (props.editable) {
            setState((state) => ({
                position: {
                    x: (props.element.x || 0),
                    y: (props.element.y || 0)
                },
                delta: undefined,
                moving: false,
                changed: false,
            }))
        }
    }, [props.element.x, props.element.y]);

    useEffect(() => {
        if (props.editable) {
            if (state.changed) {
                props.onPositionChanged({
                    x: (state.position.x || 0),
                    y: (state.position.y || 0)
                })
            }
        }
    }, [state.changed]);

    const handleChange = useCallback((moveData: any) => {
        if (props.editable) {
            setState((state) => ({
                moving: moveData.moving,
                changed: moveData.stoppedMoving && (moveData.delta.x !== 0 || moveData.delta.y !== 0),
                position: moveData.stoppedMoving
                    ? {
                        x: state.position.x + moveData.delta.x / (props.scale ?? 1),
                        y: state.position.y + moveData.delta.y / (props.scale ?? 1),
                    }
                    : state.position,
                delta: moveData.moving ? moveData.delta : undefined,
            }));
            if (props.onMove && moveData.moving) {
                const info = {
                    x: (state.position.x + moveData.delta.x),
                    y: (state.position.y + moveData.delta.y),
                    z: props.element.z || 1,
                    scale: props.element.scale || 1
                }
                props.onMove(info)

            }
        }
    }, [state.position, props.scale]);

    const ref = useMovable({onChange: handleChange});

    return (
        <div ref={props.static == true ? null : ref}
             onMouseDown={(e) => {
                 e.stopPropagation()
                 if (props.editable) {
                     props.onSelect()
                 }
             }}
             className={'element-container'}
             style={
                !props.static ? {
                     ...props.style,
                     outline: props.isSelected ? `${2 / (props.scale ? props.scale : 1) / (props.element.scale ?? 1)}px solid #1890ff` : undefined,
                     borderRadius: props.isSelected ? `${6 / (props.scale ? props.scale : 1) / (props.element.scale ?? 1)}px` : undefined,
                     cursor: props.style?.cursor || 'pointer',
                     position: 'absolute',
                     width: props.size?.width && Number.isFinite(props.size.width) ? props.size.width : 'auto',
                     height: props.size?.height && Number.isFinite(props.size.width) ? props.size.height : 'auto',
                     zIndex: props.isSelected ? 1000000 : props.element.z === 0 ? 1 : props.element.z,
                     transformOrigin: 'top left',
                     top: state.position.y || 0,
                     left: state.position.x || 0,
                     transform: (state.delta ? `translate3d(${state.delta.x / (props.scale ?? 1)}px, ${state.delta.y / (props.scale ?? 1)}px, 0)` : '') + `scale(${(props.element.scale && props.element.scale > 0 ? props.element.scale : 1)})`,
                 } : {
                    width: '100%',
                    height: '100%',
                }
             }>
            {props.children}
        </div>
    )
}

export function getOffset(target: any): Position2D {
    if (target == null) {
        return {x: 0, y: 0}
    } else {
        const offset = getOffset(target.offsetParent)
        return {x: target.offsetLeft + offset.x, y: target.offsetTop + offset.y}
    }
}
